import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BannerContext } from "components/SideBar";
import { useAppContext } from "contexts";
import { canReviewFeedback, parseJwtPayload } from "util/moduleAccess";
import FeedbackSummaryList from "./FeedbackSummaryList";
import EmployeeFeedbackScreen from "./EmployeeFeedbackScreen";
import { FeedbackTarget } from "./ManagerFeedbackForm";

/**
 * Quarterly Feedback — the module's single entry point.
 *
 * Role decides the LANDING view, not the vocabulary:
 *   reviewer (role 2 / 4 / 5) → the employee roll-up list, then drill into one
 *   everyone else            → straight to their own screen
 *
 * NOTE: a reviewer drilling into a person gets the SAME screen an employee sees
 * of themselves, so there is one component and one set of tab names for both
 * roles. The server independently enforces every permission this renders.
 */
const QuarterlyFeedback: React.FC = () => {
  const { userRole, email } = useAppContext();
  const { isBannerVisible } = useContext(BannerContext);

  // Role comes from the JWT where available, falling back to context — the
  // same precedence `canAccess` uses elsewhere in the app.
  const canReview = useMemo(() => {
    const payload = parseJwtPayload(localStorage.getItem("accessToken"));
    return canReviewFeedback(payload, userRole);
  }, [userRole]);

  /**
   * Router state carries where to resume: which employee was open, or whether
   * the viewer was on their own screen. Declared BEFORE the state below, which
   * reads it in its initializer.
   */
  const location = useLocation();
  const resumeState = (location.state as {
    selected?: FeedbackTarget;
    viewingOwn?: boolean;
  } | null) ?? null;

  const [selected, setSelected] = useState<FeedbackTarget | null>(
    // CHANGE: seeded from router state so returning from a reviewer screen
    // reopens the employee you were working on, not the bare roll-up.
    () => resumeState?.selected ?? null,
  );

  /**
   * CHANGE: reviewers had NO way to reach their own screen. `showList` sent
   * every role 2 / 4 / 5 viewer to the roll-up, and the only way off it was
   * picking SOMEONE ELSE — so an admin or manager could never raise their own
   * quarterly review request, even though they are employees too and
   * `POST /self` is always about the caller. This flag is that way out.
   *
   * NOTE: a flag rather than putting the viewer in `selected`.
   * `EmployeeFeedbackScreen` decides "own screen" from an ABSENT target, so
   * passing the viewer as a target would make it fall back to comparing emails
   * against the summary — and until that fetch resolves it would render the
   * reviewer actions (Add feedback, Approve) against the viewer themselves.
   */
  const [viewingOwn, setViewingOwn] = useState(
    // CHANGE: seeded from router state so returning from the goal-sheet screen
    // lands a reviewer back on their OWN screen. Without this, saving a sheet
    // bounced them to the team roll-up they never came from.
    Boolean(resumeState?.viewingOwn),
  );

  /** Back to the roll-up, from either an employee's screen or the viewer's own. */
  const backToList = () => {
    setSelected(null);
    setViewingOwn(false);
  };

  /**
   * CHANGE: keeps the history entry in step with what is actually on screen.
   *
   * THE BUG THIS FIXES: picking an employee is component state and performs no
   * navigation, so `history.state` kept whatever the LAST navigation put there
   * — and the only writer is ManagerFeedbackPage's goBack, which returns with
   * `{ selected: <that employee> }`. So: review Ankita, come back, click Chat,
   * reload, and the initializers above read the stale state and reopened
   * ANKITA. A reload showed an employee the user had not selected.
   *
   * `replace` rather than push, so this never adds a history entry and the
   * browser Back button still behaves — it only rewrites the state attached to
   * the entry already showing.
   *
   * NOTE: the effect cannot loop. `resumeState` is read only inside the
   * useState initializers above, which run once, so rewriting the location
   * does not feed back into `selected` or `viewingOwn`.
   */
  const navigate = useNavigate();
  useEffect(() => {
    let nextState: { selected?: FeedbackTarget; viewingOwn?: boolean } = {};
    if (selected) nextState = { selected };
    else if (viewingOwn) nextState = { viewingOwn: true };

    navigate(location.pathname, { replace: true, state: nextState });
  }, [selected, viewingOwn, navigate, location.pathname]);

  // Matches the height convention used by the other full-page listings.
  const listHeightClass = isBannerVisible
    ? "h-[calc(100vh-105px)] min-h-[300px]"
    : "h-[calc(100vh-75px)] min-h-[300px]";

  // CHANGE: `viewingOwn` added to the condition, so a reviewer who asked for
  // their own screen gets it instead of being held on the roll-up.
  const showList = canReview && !selected && !viewingOwn;

  return (
    /*
     * CHANGE: the employee branch used to be the scroller itself —
     * `overflow-y-auto` with an inline `maxHeight: calc(100vh - 75px)`.
     *
     * Two problems. The 75px was a guess at chrome that does not exist: on
     * desktop the shell hands a page EXACTLY 100vh (the top nav sits inside
     * the left sidebar column, not above the page), so the box was 75px short
     * and left a band of dead space under the last card. And because the
     * scroller wrapped EVERYTHING, the employee's name, the year picker and
     * the tab bar scrolled away with the content.
     *
     * Now it fills its box with h-full and scrolls nothing; the screen inside
     * pins its own header and scrolls only the list. The roll-up branch is
     * untouched — FeedbackSummaryList sizes its own DataTable.
     */
    <div
      className={`text-slate-900 dark:text-white bg-white dark:bg-gray-800 transition-colors duration-300 ${
        showList ? "" : "flex h-full min-h-0 flex-col overflow-hidden p-3 sm:p-4"
      }`}
    >
      {showList ? (
        <FeedbackSummaryList
          heightClass={listHeightClass}
          onSelectEmployee={setSelected}
          onViewOwnFeedback={() => setViewingOwn(true)}
        />
      ) : (
        <EmployeeFeedbackScreen
          // Remount per employee so the year, tab and any open modal reset —
          // otherwise a year picked for one person would carry to the next.
          key={selected?.userId ?? "self"}
          target={selected}
          canReview={canReview}
          viewerEmail={email}
          // CHANGE: was `canReview && selected`. A reviewer on their OWN screen
          // has no `selected`, so the back arrow disappeared and they were
          // stranded there. `canReview` alone is correct: when the list is not
          // showing and the viewer may review, they arrived from the list.
          onBack={canReview ? backToList : undefined}
        />
      )}
    </div>
  );
};

export default QuarterlyFeedback;
